import { isUploadLinkActive } from '@/lib/api/uploadLinks'
import type { AccessRole, TemplateAccessEntry, UploadLink } from '@/types/domain'

/**
 * The logic behind the Freigeben dialog, kept out of the component so the
 * parts a click would otherwise be needed to reach — what a refused role
 * change means, who may be removed, what an upload link's state is — can be
 * tested directly.
 */

export const ROLE_LABELS: Record<AccessRole, string> = {
  read: 'Lesen',
  write: 'Bearbeiten',
  admin: 'Verwalten',
}

/**
 * The ladder, weakest first.
 *
 * The artboard's picker lists Bearbeiten · Lesen · Verwalten — the default
 * first rather than in order. A three-item list whose middle entry is the
 * weakest is a list people misread, so the order here is the ladder and the
 * *selected* value is still Bearbeiten. It is also the order the legend under
 * the list explains the roles in, and the two disagreeing would be worse.
 */
export const ROLE_ORDER: AccessRole[] = ['read', 'write', 'admin']

/** What each role may do — the legend under the member list. The artboard's
 *  wording, which is also the one the gateway implements: `writeMW` does
 *  allow PATCHing the template, so "Vorlage ändern" belongs to Bearbeiten and
 *  the Flutter app's copy (which puts it under Verwalten) is stale. */
export const ROLE_EXPLANATIONS: { role: AccessRole; text: string }[] = [
  { role: 'read', text: 'Dokumente und Ergebnisse ansehen, exportieren' },
  { role: 'write', text: 'zusätzlich hochladen, prüfen, Vorlage ändern' },
  { role: 'admin', text: 'zusätzlich freigeben und archivieren' },
]

export type ShareRowKind = 'owner' | 'direct' | 'folder'

export interface ShareRow {
  userId: string
  /** Name where the users service knew one, else the address, else the uid. */
  name: string
  email?: string
  initials: string
  role: AccessRole
  kind: ShareRowKind
  isSelf: boolean
}

/** Initials for the avatar: first and last initial of a display name, falling
 *  back to the local part of the address. Mirrors `initialsFor` in
 *  `useCockpit`, which is not exported from a file this wave may import. */
export function initialsFor(entry: { name?: string; email?: string }): string {
  const source = (entry.name ?? '').trim() || (entry.email ?? '').split('@')[0] || ''
  const parts = source.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function kindOf(entry: TemplateAccessEntry): ShareRowKind {
  if (entry.owner) return 'owner'
  return entry.source === 'folder' ? 'folder' : 'direct'
}

const KIND_ORDER: Record<ShareRowKind, number> = { owner: 0, direct: 1, folder: 2 }

/**
 * The member list as the dialog draws it: the owner, then the people invited
 * here, then the ones who come with the project — because that is the order
 * in which the rows stop being editable, and a list whose controls appear and
 * disappear at random looks broken.
 */
export function shareRows(entries: TemplateAccessEntry[], selfId?: string): ShareRow[] {
  return entries
    .map((entry) => ({
      userId: entry.userId,
      name: (entry.name ?? '').trim() || entry.email || entry.userId,
      email: entry.email,
      initials: initialsFor(entry),
      role: entry.role,
      kind: kindOf(entry),
      isSelf: selfId != null && entry.userId === selfId,
    }))
    .sort((a, b) => {
      const byKind = KIND_ORDER[a.kind] - KIND_ORDER[b.kind]
      if (byKind !== 0) return byKind
      return a.name.localeCompare(b.name, 'de')
    })
}

/** Only a direct grant can be changed here. The owner's access cannot be
 *  taken away at all (the gateway answers 409 `owner_access_required`), and a
 *  folder membership is not an entry on this template. */
export function isEditable(row: ShareRow): boolean {
  return row.kind === 'direct'
}

/**
 * What to say when a role change was refused, and whether an invitation is
 * the way out of it.
 *
 * The gateway now tells three refusals apart that used to arrive as one, and
 * the difference is the whole point of this function:
 *  - `access_entry_not_found` (404) — the person has no *direct* entry on
 *    this template. They may still reach it through the project. Giving them
 *    a role of their own is an invitation, not a role change, so the dialog
 *    offers that instead of reporting a failure;
 *  - `owner_access_required` (409) — the owner keeps their access;
 *  - anything else, **500 included**: the lookup itself failed. It used to
 *    come back as a 404, so a dropped connection read as "their access was
 *    removed". It must not: nothing is known about the entry, and the copy
 *    says only that the role did not change.
 *
 * The German sentences for the first three come from `mapAccessError` in
 * `lib/api/access.ts`, which has already rewritten them by the time an error
 * reaches here.
 */
export interface RoleChangeRefusal {
  message: string
  /** The entry is missing, so an invitation is what the person wants. */
  offerInvite: boolean
}

export function roleChangeRefusal(error: unknown): RoleChangeRefusal {
  const api = error as { code?: string; message?: string; status?: number } | null
  const code = api?.code
  if (code === 'access_entry_not_found') {
    return {
      message:
        api?.message ||
        'Diese Person hat keine eigene Freigabe für diese Vorlage. Laden Sie sie ein, um ihr eine Rolle zu geben.',
      offerInvite: true,
    }
  }
  if (code === 'owner_access_required' || api?.status === 403) {
    return { message: api?.message || 'Die Rolle wurde nicht geändert.', offerInvite: false }
  }
  // Including 500: the gateway could not look the entry up. Saying anything
  // about the entry here would be a guess about somebody's access.
  return {
    message: 'Die Rolle wurde nicht geändert. Bitte versuchen Sie es erneut.',
    offerInvite: false,
  }
}

// -- Upload links ----------------------------------------------------------

export type UploadLinkState = 'active' | 'revoked' | 'expired'

export function uploadLinkState(link: UploadLink, now: Date = new Date()): UploadLinkState {
  if (link.revokedAt) return 'revoked'
  return isUploadLinkActive(link, now) ? 'active' : 'expired'
}

export const UPLOAD_LINK_STATE_LABELS: Record<UploadLinkState, string> = {
  active: 'aktiv',
  revoked: 'zurückgezogen',
  expired: 'abgelaufen',
}

/** The links worth showing, newest first, dead ones last: a revoked link is
 *  kept by the gateway forever and there is no point burying the working one
 *  under six of them. */
export function sortedLinks(links: UploadLink[], now: Date = new Date()): UploadLink[] {
  return [...links].sort((a, b) => {
    const stateA = uploadLinkState(a, now) === 'active' ? 0 : 1
    const stateB = uploadLinkState(b, now) === 'active' ? 0 : 1
    if (stateA !== stateB) return stateA - stateB
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })
}

export function formatDate(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// -- Refusals --------------------------------------------------------------

/** The gateway sends `user_not_found` in `error`, not in `code`, so both are
 *  checked. See `mapAccessError` in `lib/api/access.ts`, which already
 *  rewrites it — this catches the raw form too. */
export function isUserNotFound(error: unknown): boolean {
  const api = error as { code?: string; message?: string; status?: number } | null
  return api?.code === 'user_not_found' || api?.message === 'user_not_found'
}

export function isEmailUnverified(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === 'email_unverified'
}

/**
 * What to say when inviting failed.
 *
 * `user_not_found` is the ordinary outcome, not a breakdown: there are no
 * e-mail invitations, so an address without an account cannot be given
 * access. The Flutter app's wording is kept because it is the honest one.
 */
export function inviteErrorMessage(error: unknown): string {
  if (isUserNotFound(error)) {
    return 'Kein Konto mit dieser Adresse. Die Person muss sich zuerst registrieren.'
  }
  const api = error as { status?: number; message?: string } | null
  if (api?.status === 403) return 'Sie dürfen die Freigabe dieser Vorlage nicht ändern.'
  return api?.message || 'Die Person konnte nicht hinzugefügt werden.'
}
